import { withDevtools } from "@angular-architects/ngrx-toolkit";
import { computed, effect, inject, ResourceRef } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { ProfilDTO, QuestionDTO, TypeParcours } from "@mra/hlp9002";
import { getState, patchState, signalStore, withComputed, withHooks, withMethods, withState } from "@ngrx/signals";
import { map, Observable, tap } from "rxjs";
import { AffichageInfos } from "src/app/models/affichageInfos";
import { CODE_FINAL_QUESTION, QuestionReponse } from "src/app/models/questionReponse.model";
import { LocalStorageService, StateKey } from "../shared/services/local-storage.service";
import { QuestionnaireService } from "../shared/services/questionnaire.service";
import { SimuUtils } from "../utils/simu-utils";
import { debugStore } from "./debug.store";
import { errorsStore } from "./errors.store";
import { ChangementQuestion, QuestionnaireState } from "./state";
import { userStore } from "./user.store";

function createDefaultState(): QuestionnaireState {
    return {
        typeParcours: TypeParcours.SIMPLE,
        questionsReponses: [],
        affichageInfos: null,
        profil: null
    };
}

// eslint-disable-next-line @typescript-eslint/typedef
export const questionnaireStore = signalStore(
    { providedIn: "root" },
    withDevtools("questionnaire"),
    withState(createDefaultState()),
    withComputed((state) => ({
        getAllResponses: computed(() => {
            const questRes: QuestionReponse[] = state.questionsReponses();
            if (state.typeParcours() !== TypeParcours.COMPLET || !questRes) {
                return "";
            }
            return questRes
                .filter((res) => res.questionCode !== CODE_FINAL_QUESTION)
                .reduce((acc, res) => {
                    if (!res.reponseCode) return acc;
                    const separator: string = acc ? "_" : "";
                    const numeroQuestion: number = Number.parseInt(res.questionCode?.toLowerCase().replace("q", ""));
                    acc += separator + numeroQuestion + res.reponseCode.toLowerCase();
                    return acc;
                }, "");
        }),
        questionActuelle: computed(() => state.questionsReponses()?.find((qr) => qr.isQuestionActuelle)),
        profilage: computed(() => state.profil())
    })),

    withMethods((store, questionnaireService = inject(QuestionnaireService), user = inject(userStore), errors = inject(errorsStore), debug = inject(debugStore)) => ({
        retrieveState(savedState: QuestionnaireState): void {
            patchState(store, () => savedState ?? createDefaultState());
        },

        getState() {
            return {
                typeParcours: store.typeParcours(),
                allResponses: store.getAllResponses(),
                questionsReponses: store.questionsReponses()
            };
        },

        setProfil(profil: ProfilDTO): void {
            patchState(store, () => ({ profil }));
        },

        unsetSimulation(): void {
            patchState(store, () => createDefaultState());
        },

        changeQuestion(props: ChangementQuestion): void {
            patchState(store, (state) => {
                const oldTableau: QuestionReponse[] = state.questionsReponses ?? [];
                let existingRes: QuestionReponse = null;
                let nouveauTableau: QuestionReponse[] = oldTableau.map((questRes) => {
                    //--- enregistre la réponse si la question existe dans l'historique
                    if (questRes.questionCode === props.codeQuestionActuelle) {
                        existingRes = { ...questRes };
                        questRes.reponseCode = props.reponseActuelle;
                        if (debug.isModeDebugActivated()) {
                            questRes.questionLibelle = props.questionLibelle;
                            questRes.reponseLibelle = props.reponseLibelle;
                        }
                    }
                    return { ...questRes, isQuestionActuelle: false };
                });

                //--- Si la réponse n'existe pas, on l'ajoute à la liste
                if (!existingRes) {
                    const question: QuestionReponse = {
                        questionCode: props.codeQuestionActuelle,
                        reponseCode: props.reponseActuelle,
                        isQuestionActuelle: false
                    };
                    if (debug.isModeDebugActivated()) {
                        question.questionLibelle = props.questionLibelle;
                        question.reponseLibelle = props.reponseLibelle;
                    }
                    nouveauTableau.push(question);
                }
                //--- Sinon, si elle a changé, le déroulé des questions aussi, donc on vide les autres réponses
                else if (existingRes.reponseCode !== props.reponseActuelle) {
                    let questionCode: string = props.codeQuestionActuelle;
                    while (questionCode) {
                        const toFind: string = questionCode;
                        questionCode = null;
                        nouveauTableau = nouveauTableau.filter((oldQuestRes) => {
                            if (oldQuestRes.questionPrecedenteCode === toFind) {
                                questionCode = oldQuestRes.questionCode;
                                return false;
                            }
                            return true;
                        });
                    }
                }

                //--- passe à la nouvelle question
                const nextQuestDoesntExist: boolean = !nouveauTableau.some((curr) => curr.questionCode === props.nextQuestionCode);
                if (props.nextQuestionCode && nextQuestDoesntExist) {
                    nouveauTableau.push({
                        questionCode: props.nextQuestionCode,
                        reponsePrecedente: props.reponseActuelle,
                        questionPrecedenteCode: props.codeQuestionActuelle,
                        reponseCode: null,
                        isQuestionActuelle: true
                    });
                } else {
                    nouveauTableau = nouveauTableau.map((questRes) => ({
                        ...questRes,
                        isQuestionActuelle: questRes.questionCode === props.nextQuestionCode
                    }));
                }

                return { questionsReponses: nouveauTableau };
            });
        },

        questionPrecedente(): void {
            patchState(store, (state) => {
                const actuelle: QuestionReponse = state.questionsReponses?.find((curr) => curr.isQuestionActuelle);
                return {
                    questionsReponses: state.questionsReponses?.map((curr) => ({
                        ...curr,
                        isQuestionActuelle: curr.questionCode === actuelle.questionPrecedenteCode
                    }))
                };
            });
        },

        setAffichageInfos(affichageInfos: AffichageInfos): void {
            patchState(store, () => ({ affichageInfos: affichageInfos }));
        },

        naviguerNext(): void {
            patchState(store, (state) => {
                let newIndex: number = (state.affichageInfos?.pageCourante ?? 0) + 1;
                if (newIndex > state.affichageInfos?.ecrans?.length - 1) {
                    newIndex = state.affichageInfos?.pageCourante ?? 0;
                }
                return changeIndexInState(state, newIndex);
            });
        },

        naviguerPrev(): void {
            patchState(store, (state) => {
                let newIndex: number = state.affichageInfos.pageCourante - 1;
                if (newIndex < 0) {
                    newIndex = 0;
                }
                return changeIndexInState(state, newIndex);
            });
        },

        setLastAffichageInfoIndex(): void {
            patchState(store, (state) => {
                return changeIndexInState(state, state.affichageInfos.ecrans.length - 1);
            });
        },

        getNumeroQuestion: (codeQuestion: string) =>
            computed(() => {
                const questRes: QuestionReponse[] = store.questionsReponses();
                if (codeQuestion === CODE_FINAL_QUESTION) return 0;
                let numero: number = 0;
                let codeExamine: string = codeQuestion;
                while (codeExamine) {
                    const question: QuestionReponse = questRes?.find((quest) => quest.questionCode === codeExamine);
                    codeExamine = question?.questionPrecedenteCode;
                    numero++;
                }
                return numero;
            }),

        createQuestionActuelleResource: (): ResourceRef<QuestionDTO> => {
            return rxResource({
                params: computed(() => store.questionActuelle() ?? null),
                stream: ({ params: questRes }) => {
                    const dateEffet: string = SimuUtils.getDateEffet(null, user.simulation());
                    const debugDisplay: boolean = debug.isModeDebugActivated();
                    const questionObs: Observable<QuestionDTO> = questRes?.questionPrecedenteCode
                        ? questionnaireService.getNextQuestion(questRes?.questionPrecedenteCode, questRes?.reponsePrecedente, dateEffet, user.epargneCumulee(), debugDisplay)
                        : questionnaireService.getFirstQuestion(dateEffet, user.epargneCumulee(), debugDisplay);
                    return questionObs.pipe(
                        map((question) => (question.question ? question : createEndParcoursQuestion(question))),
                        tap({ error: (e) => errors.addError(e as Error, `Impossible de récupérer les détails de la question`) })
                    );
                }
            });
        },

        getResponse: (codeQuestion: string) =>
            computed(() => {
                return store.questionsReponses()?.find((qr) => qr.questionCode === codeQuestion);
            }),

        setTypeParcours(typeParcours: TypeParcours): void {
            patchState(store, () => ({ typeParcours }));
        },

        returnToFirstQuestion(): void {
            patchState(store, (state) => ({
                questionsReponses: (state.questionsReponses ?? []).map((questRes) => {
                    return { ...questRes, isQuestionActuelle: !questRes.questionPrecedenteCode };
                })
            }));
        }
    })),
    withHooks({
        onInit(store) {
            const localStorageService: LocalStorageService = inject(LocalStorageService);

            const savedState: QuestionnaireState = localStorageService.getSavedState<QuestionnaireState>(StateKey.QUESTIONNAIRE);
            if (savedState) store.retrieveState(savedState);
            effect(() => {
                const state: QuestionnaireState = getState(store);
                localStorageService.saveState(StateKey.QUESTIONNAIRE, state);
            });
        }
    })
);

const changeIndexInState: (state: QuestionnaireState, newIndex: number) => Partial<QuestionnaireState> = (state: QuestionnaireState, newIndex: number) => {
    return {
        affichageInfos: {
            ...state.affichageInfos,
            pageCourante: newIndex
        }
    };
};

/**
 * génére la question "finale" qui permet à l'utilisateur d'afficher des pages d'explications ou non
 * @param sourceQuestion - question précédente
 * @returns question finale
 */
export function createEndParcoursQuestion(sourceQuestion: QuestionDTO): QuestionDTO {
    return {
        code: CODE_FINAL_QUESTION,
        question: null,
        reponses: sourceQuestion.parcoursTypes.map((parcoursType) => {
            return {
                id: "",
                valeur: "",
                questionSuivante: "",
                ...parcoursType,
                libelle: ""
            };
        }),
        titre: null,
        valeur: 0,
        parcoursTypes: []
    };
}
